import { ExclamationCircleFilled, PlusOutlined } from '@ant-design/icons';
import { Button, Modal as AntdModal, Table, Modal, Form, InputNumber, Select, Radio, Checkbox, Space, Divider, Spin, Tag } from 'antd';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import AddTauxCommission from './AddTauxCommission';
import ActionDropdown from '../../production/components/ActionDropdown';
import axiosInstance from '../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../variables/constants';

const { confirm } = AntdModal;

// ─── Composant réutilisable : bloc de sélection (Tout / Aucun / Personnalisé) ─
const SelectionBlock = ({
  title,
  icon,
  selectionType,
  onChangeType,
  items,
  loading,
  selectedItems,
  onChangeItems,
  itemKey,        // champ servant de clé unique
  itemLabel,      // fn (item) => string affiché comme label principal
  itemSub,        // fn (item) => string affiché comme sous-label (optionnel)
  previewLabel,   // label utilisé dans l'aperçu "Toutes"
  colors = {},
}) => {
  const accentColor = colors.primary || '#242166';

  return (
    <div style={{
      border: `1px solid ${selectionType !== 'none' ? accentColor : '#e8e8e8'}`,
      borderRadius: 10,
      padding: 16,
      background: selectionType !== 'none' ? '#f5f5ff' : '#fafafa',
      transition: 'all 0.25s ease'
    }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span style={{ fontWeight: 600, color: accentColor, fontSize: 14 }}>{title}</span>
        <Tag color={selectionType !== 'none' ? 'processing' : 'default'} style={{ marginLeft: 'auto', borderRadius: 12 }}>
          {selectionType === 'all' && `Tous (${items.length})`}
          {selectionType === 'none' && 'Aucun'}
          {selectionType === 'custom' && `${selectedItems.length} / ${items.length}`}
        </Tag>
      </div>

      <Radio.Group
        value={selectionType}
        onChange={(e) => onChangeType(e.target.value)}
        style={{ width: '100%' }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Radio value="all">
            <span style={{ fontWeight: 500 }}>Tous</span>
            <div style={{ fontSize: 12, color: '#666', marginLeft: 24 }}>
              Appliquer à {loading ? '...' : `${items.length} ${previewLabel}`}
            </div>
          </Radio>
          <Radio value="none">
            <span style={{ fontWeight: 500 }}>Aucun</span>
            <div style={{ fontSize: 12, color: '#666', marginLeft: 24 }}>
              Ne pas propager les modifications
            </div>
          </Radio>
          <Radio value="custom">
            <span style={{ fontWeight: 500 }}>Sélection personnalisée</span>
            <div style={{ fontSize: 12, color: '#666', marginLeft: 24 }}>
              Choisir individuellement
            </div>
          </Radio>
        </Space>
      </Radio.Group>

      {/* Aperçu "Tous" */}
      {selectionType === 'all' && !loading && items.length > 0 && (
        <div style={{
          marginTop: 12,
          background: '#fff',
          border: '1px solid #e8e8e8',
          borderRadius: 6,
          padding: '8px 12px',
          fontSize: 13
        }}>
          <strong>Aperçu :</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
            {items.slice(0, 5).map(item => (
              <li key={item[itemKey]}>{itemLabel(item)}</li>
            ))}
            {items.length > 5 && <li style={{ color: '#888' }}>… et {items.length - 5} autre{items.length - 5 > 1 ? 's' : ''}</li>}
          </ul>
        </div>
      )}

      {/* Sélection personnalisée */}
      {selectionType === 'custom' && (
        <div style={{ marginTop: 12 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <Spin size="small" /> <span style={{ marginLeft: 8, color: '#888' }}>Chargement…</span>
            </div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 16, color: '#888', fontSize: 13 }}>
              Aucun élément disponible
            </div>
          ) : (
            <>
              {/* Sélectionner tout / Désélectionner tout */}
              <div style={{ marginBottom: 8, display: 'flex', gap: 8 }}>
                <Button
                  size="small"
                  onClick={() => onChangeItems(items.map(i => i[itemKey]))}
                  disabled={selectedItems.length === items.length}
                >
                  Tout sélectionner
                </Button>
                <Button
                  size="small"
                  onClick={() => onChangeItems([])}
                  disabled={selectedItems.length === 0}
                >
                  Tout désélectionner
                </Button>
              </div>
              <Checkbox.Group
                value={selectedItems}
                onChange={onChangeItems}
                style={{ width: '100%' }}
              >
                <div style={{
                  maxHeight: 240,
                  overflowY: 'auto',
                  border: '1px solid #e8e8e8',
                  borderRadius: 6,
                  padding: '4px 8px'
                }}>
                  {items.map(item => (
                    <div
                      key={item[itemKey]}
                      style={{
                        padding: '6px 4px',
                        borderBottom: '1px solid #f5f5f5',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Checkbox value={item[itemKey]} style={{ width: '100%' }}>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: 13 }}>{itemLabel(item)}</div>
                          {itemSub && (
                            <div style={{ fontSize: 11, color: '#888' }}>{itemSub(item)}</div>
                          )}
                        </div>
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </Checkbox.Group>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const TauxCommissions = () => {
  const [tauxCommissions, setTauxCommissions] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedTauxCommission, setSelectedTauxCommission] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [initialModalData, setInitialModalData] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [typeIntermediaire, setTypeIntermediaire] = useState('');

  // ── État du modal de propagation ──────────────────────────────────────────
  const [isAgencyModalVisible, setIsAgencyModalVisible] = useState(false);
  const [pendingCommissionData, setPendingCommissionData] = useState(null);

  // Agences
  const [agencies, setAgencies] = useState([]);
  const [loadingAgencies, setLoadingAgencies] = useState(false);
  const [agencySelectionType, setAgencySelectionType] = useState('all');
  const [selectedAgencies, setSelectedAgencies] = useState([]);

  // Intermédiaires
  const [intermediaires, setIntermediaires] = useState([]);
  const [loadingIntermediaires, setLoadingIntermediaires] = useState(false);
  const [intermediaireSelectionType, setIntermediaireSelectionType] = useState('all');
  const [selectedIntermediaires, setSelectedIntermediaires] = useState([]);
  // ─────────────────────────────────────────────────────────────────────────

  const [form] = Form.useForm();

  const colors = {
    primary: '#242166',
    secondary: '#1890ff',
    success: '#52c41a',
    error: '#ff4d4f',
    gradient: 'linear-gradient(135deg, #242166 0%, #1890ff 100%)',
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const hasTauxChanged = (currentData, initialData) => {
    if (!initialData || initialData.length === 0) return true;
    return currentData.some((current, index) => {
      const initial = initialData[index];
      return current.taux !== initial.taux;
    });
  };

  const parseDate = (d) => (d ? new Date(d).getTime() : 0);
  const sortByNewest = (arr) =>
    [...(arr || [])].sort(
      (a, b) => parseDate(b?.updatedAt ?? b?.createdAt) - parseDate(a?.updatedAt ?? a?.createdAt)
    );

  const logAxiosError = (err, label) => {
    console.group(` ${label}`);
    console.error('message:', err);
  };

  // ── Fetch agences ─────────────────────────────────────────────────────────
  const fetchAgenciesByType = async (typeInter) => {
    setLoadingAgencies(true);
    try {
      const response = await axiosInstance.get(`/api/agence/intermediaire/${typeInter}`);
      setAgencies(response.data || []);
    } catch (err) {
      console.error('Erreur lors de la récupération des agences:', err);
      toast.error('Erreur lors de la récupération des agences');
      setAgencies([]);
    } finally {
      setLoadingAgencies(false);
    }
  };

  // ── Fetch intermédiaires par type ─────────────────────────────────────────
  const fetchIntermediairesByType = async (typeInter) => {
    setLoadingIntermediaires(true);
    try {
      const response = await axiosInstance.get(`/api/intermediaire?type=${typeInter}`);
      // Filtrer sur le type côté client si l'API renvoie tout
      const data = (response.data || []).filter(i =>
        !typeInter || i.type === typeInter || i.typeIntermediaire === typeInter
      );
      console.log(data);
      setIntermediaires(data);
    } catch (err) {
      console.error('Erreur lors de la récupération des intermédiaires:', err);
      toast.error('Erreur lors de la récupération des intermédiaires');
      setIntermediaires([]);
    } finally {
      setLoadingIntermediaires(false);
    }
  };

  // ── Fetch taux commissions ────────────────────────────────────────────────
  const fetchTauxCommissions = useCallback(() => {
    setLoadingTable(true);
    axiosInstance
      .get(`/api/taux-commission`)
      .then((res) => setTauxCommissions(sortByNewest(res.data || [])))
      .catch((err) => logAxiosError(err, 'GET /api/taux-commission'))
      .finally(() => setLoadingTable(false));
  }, []);

  useEffect(() => {
    fetchTauxCommissions();
    axiosInstance.get(`/api/categorie`).then((res) => {
      setAllCategories(res.data?.filter((c) => !c.codeCategorie?.startsWith("V")));
    }).catch((err) => logAxiosError(err, 'GET /api/categorie'));
  }, [fetchTauxCommissions]);

  // ── handleSave : valide le formulaire taux puis ouvre le modal propagation ─
  const handleSave = () => {
    form.validateFields()
      .then(values => {
        const updatedModalData = modalData.map(item => ({
          ...item,
          taux: values[item.categorie.codeCategorie],
          organisme: LOGGED_USER?.profil?.organisme?.code,
          typeIntermediaire: editMode ? selectedTauxCommission.typeIntermediaire : typeIntermediaire,
        }));

        if (!hasTauxChanged(updatedModalData, initialModalData)) {
          toast.info("Aucune modification détectée");
          setIsModalVisible(false);
          return;
        }

        const stc = {
          intermediaireId: editMode ? selectedTauxCommission.typeIntermediaire : typeIntermediaire,
          organismeCode: editMode
            ? selectedTauxCommission.organisme?.code
            : LOGGED_USER?.profil?.organisme?.code,
          commissions: updatedModalData
        };

        setPendingCommissionData(stc);
        setIsModalVisible(false);

        const typeInter = selectedTauxCommission?.typeIntermediaire || typeIntermediaire;

        // Chargement parallèle agences + intermédiaires
        Promise.all([
          fetchAgenciesByType(typeInter),
          fetchIntermediairesByType(typeInter),
        ]).then(() => {
          setIsAgencyModalVisible(true);
        });
      })
      .catch(error => console.error('Erreur validation formulaire :', error));
  };

  // ── Confirmation finale : envoie agences + intermédiaires ─────────────────
  const handleAgencySelectionConfirm = () => {
    if (!pendingCommissionData) return;

    const agenciesToApply =
      agencySelectionType === 'all' ? agencies.map(a => a.codeAgence || a.codeId)
        : agencySelectionType === 'custom' ? selectedAgencies
          : [];

    const intermediairesToApply =
      intermediaireSelectionType === 'all' ? intermediaires.map(i => i.codeIntermediaire || i.codeId)
        : intermediaireSelectionType === 'custom' ? selectedIntermediaires
          : [];

    const finalData = {
      ...pendingCommissionData,
      agencyCodes: agenciesToApply,
      intermediaireCodes: intermediairesToApply,
      intermediaireId: selectedTauxCommission?.typeIntermediaire || typeIntermediaire,
    };

    axiosInstance.put(`/api/taux-commission/parametered/taux-agences`, finalData)
      .then(() => {
        const parts = [];
        if (agenciesToApply.length > 0) parts.push(`${agenciesToApply.length} agence(s)`);
        if (intermediairesToApply.length > 0) parts.push(`${intermediairesToApply.length} intermédiaire(s)`);
        const detail = parts.length > 0 ? ` pour ${parts.join(' et ')}` : '';
        toast.success(`Taux de commission mis à jour avec succès${detail}`);
        fetchTauxCommissions();
        resetPropagationModal();
      })
      .catch(err => {
        console.error(err);
        toast.error("Erreur lors de la mise à jour des taux de commission");
        resetPropagationModal();
      });
  };

  const resetPropagationModal = () => {
    setIsAgencyModalVisible(false);
    setAgencySelectionType('all');
    setSelectedAgencies([]);
    setIntermediaireSelectionType('all');
    setSelectedIntermediaires([]);
    setPendingCommissionData(null);
  };

  const handleCancel = () => setIsModalVisible(false);

  // ── CRUD helpers ──────────────────────────────────────────────────────────
  const handleShow = (edit = false, taux = null) => {
    setEditMode(edit);
    setSelectedTauxCommission(taux);
    setShowModal(true);
  };

  const handleClose = (savedItem) => {
    setShowModal(false);
    setEditMode(false);
    setSelectedTauxCommission(null);
    if (savedItem) fetchTauxCommissions();
  };

  const showDeleteConfirm = (codeId) => {
    if (!codeId) { toast.error('codeId introuvable'); return; }
    confirm({
      title: 'Êtes-vous sûr de vouloir supprimer ce taux ?',
      icon: <ExclamationCircleFilled />,
      content: 'Cette action est irréversible.',
      okText: 'Oui', okType: 'danger', cancelText: 'Annuler',
      onOk() { deleteTauxCommission(codeId); },
    });
  };

  const deleteTauxCommission = (codeId) => {
    axiosInstance.delete(`/api/taux-commission/${codeId}`)
      .then(() => { toast.success('Suppression réussie'); fetchTauxCommissions(); })
      .catch(err => { logAxiosError(err, `DELETE /api/taux-commission/${codeId}`); toast.error('Erreur lors de la suppression'); });
  };

  // ── Données groupées ──────────────────────────────────────────────────────
  const dataSourceGroupedByIntermediaireAndCompagnie = useMemo(() => {
    const groupedData = {};
    tauxCommissions.forEach(item => {
      const key = `${item.typeIntermediaire}-${item?.organisme?.code ?? ''}`;
      if (!groupedData[key]) {
        groupedData[key] = { key, codeId: item.codeId, typeIntermediaire: item.typeIntermediaire, organisme: item.organisme, details: [] };
      }
      groupedData[key].details.push(item);
    });
    return Object.values(groupedData);
  }, [tauxCommissions]);

  // ── Édition/ajout ─────────────────────────────────────────────────────────
  const handleEditClick = (record) => {
    setSelectedTauxCommission(record);
    const existingRatesMap = new Map(record.details.map(item => [item.categorie.codeCategorie, item]));
    const md = allCategories.map(cat => {
      const existingRate = existingRatesMap.get(cat.codeCategorie);
      return {
        codeId: existingRate?.codeId || `${record.key}-${cat.codeCategorie}`,
        categorie: cat,
        taux: existingRate?.taux ?? 0,
        typeIntermediaire: record.typeIntermediaire,
        organisme: record.organisme,
      };
    });
    setModalData(md);
    setInitialModalData(JSON.parse(JSON.stringify(md)));
    setIsModalVisible(true);
  };

  const handleAddClick = () => {
    setSelectedTauxCommission({});
    const md = allCategories.map(cat => ({
      codeId: cat?.categorieId || `$${cat.codeCategorie}`,
      categorie: cat,
      taux: 0,
      typeIntermediaire: typeIntermediaire,
      organisme: LOGGED_USER?.profil?.organisme?.code,
    }));
    setModalData(md);
    setInitialModalData([]);
    setIsModalVisible(true);
  };

  useEffect(() => {
    if (modalData?.length > 0) {
      form.resetFields();
      form.setFieldsValue(Object.fromEntries(modalData.map(item => [item.categorie.codeCategorie, item.taux])));
    }
  }, [modalData, form]);

  // ── Colonnes ──────────────────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      title: "Type d'intermédiaire",
      dataIndex: 'typeIntermediaire',
      key: 'typeIntermediaire',
      render: (v) => v || 'N/A',
      sorter: (a, b) => (a.typeIntermediaire || '').localeCompare(b.typeIntermediaire || ''),
    },
    {
      title: 'Compagnie',
      dataIndex: 'organisme',
      key: 'organisme',
      render: (org) => org?.raisonSociale ?? 'N/A',
      sorter: (a, b) => (a.organisme?.raisonSociale || '').localeCompare(b.organisme?.raisonSociale || ''),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <Button type="link" icon={<i className="bx bx-edit-alt" />} onClick={() => handleEditClick(record)} />
      ),
    },
  ], [handleEditClick]);

  const expandedRowColumns = [
    {
      title: 'Catégorie',
      dataIndex: 'categorie',
      key: 'categorie',
      render: (cat) => <span className="badge bg-primary">{cat?.nom ?? cat?.libelle ?? 'N/A'}</span>,
    },
    {
      title: 'Taux',
      dataIndex: 'taux',
      key: 'taux',
      render: (value) => `${value ?? 0} %`,
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <ActionDropdown
          actions={[
            { key: 'edit', label: 'Modifier', icon: <i className="bx bx-edit-alt" />, onClick: () => handleShow(true, record) },
            { key: 'delete', label: 'Supprimer', icon: <i className="bx bx-trash" />, danger: true, onClick: () => showDeleteConfirm(record?.codeId) },
          ]}
        />
      ),
    },
  ];

  const INTERMEDIAIRE_TYPES = ['COURTIER', 'AGENT_GENERAL', 'APPORTEUR_REGULIER', 'APPORTEUR_OCCASIONNEL'];

  // ── Résumé de propagation ─────────────────────────────────────────────────
  const propagationSummary = useMemo(() => {
    const agCount = agencySelectionType === 'all' ? agencies.length
      : agencySelectionType === 'custom' ? selectedAgencies.length : 0;
    const intCount = intermediaireSelectionType === 'all' ? intermediaires.length
      : intermediaireSelectionType === 'custom' ? selectedIntermediaires.length : 0;
    return { agCount, intCount, total: agCount + intCount };
  }, [agencySelectionType, agencies, selectedAgencies, intermediaireSelectionType, intermediaires, selectedIntermediaires]);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={'TARIFICATION'} />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{ marginBottom: 16 }}
                onClick={handleAddClick}
              >
                Ajouter un taux de commission
              </Button>

              <div className="card">
                <Table
                  columns={columns}
                  dataSource={dataSourceGroupedByIntermediaireAndCompagnie}
                  loading={loadingTable}
                  rowKey={(r) => r.key}
                  pagination={{ showSizeChanger: true }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <Table
                        columns={expandedRowColumns}
                        dataSource={record.details}
                        pagination={false}
                        rowKey={(r) => r?.codeId ?? r?.__localId}
                      />
                    ),
                    rowExpandable: (record) => record.details.length > 0,
                  }}
                />

                {/* ── Modal édition/ajout des taux ── */}
                <Modal
                  title={
                    selectedTauxCommission?.typeIntermediaire
                      ? `Édition des taux pour ${modalData[0]?.typeIntermediaire} (${modalData[0]?.organisme?.raisonSociale})`
                      : `Définir des taux de commissions`
                  }
                  visible={isModalVisible}
                  onOk={handleSave}
                  onCancel={handleCancel}
                  okText="Suivant ›"
                  cancelText="Annuler"
                  width={800}
                  zIndex={2000}
                >
                  <Form form={form}>
                    {!selectedTauxCommission?.typeIntermediaire && (
                      <>
                        <p>Sélectionner le type d'intermédiaire</p>
                        <Select
                          value={typeIntermediaire}
                          onChange={setTypeIntermediaire}
                          options={INTERMEDIAIRE_TYPES.map((inter, idx) => ({ label: inter, value: inter, key: idx }))}
                          style={{ width: '100%', marginBottom: 16 }}
                          placeholder="Sélectionner le type d'intermédiaire"
                        />
                      </>
                    )}
                    <Table
                      dataSource={modalData}
                      pagination={false}
                      rowKey={record => record.categorie.codeCategorie}
                      columns={[
                        {
                          title: 'Catégorie',
                          dataIndex: 'categorie',
                          key: 'categorie',
                          render: (cat) => <span className="badge bg-primary">{cat?.nom ?? cat?.libelle ?? 'N/A'}</span>,
                        },
                        {
                          title: 'Taux (%)',
                          dataIndex: 'taux',
                          key: 'taux',
                          render: (value, record) => (
                            <Form.Item
                              name={record.categorie.codeCategorie}
                              style={{ margin: 0 }}
                              rules={[{ required: true, message: 'Le taux est requis' }]}
                            >
                              <InputNumber min={0} max={100} style={{ width: '100%' }} />
                            </Form.Item>
                          ),
                        },
                      ]}
                    />
                  </Form>
                </Modal>

                {/* ── Modal propagation agences + intermédiaires ── */}
                <Modal
                  title={
                    <div style={{ borderBottom: `2px solid ${colors.primary}`, paddingBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: 16, color: colors.primary }}>
                        📡 Propager les nouveaux taux
                      </div>
                      <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                        Choisissez à qui appliquer les modifications pour le type{' '}
                        <strong>{selectedTauxCommission?.typeIntermediaire || typeIntermediaire}</strong>
                      </div>
                    </div>
                  }
                  visible={isAgencyModalVisible}
                  onOk={handleAgencySelectionConfirm}
                  onCancel={resetPropagationModal}
                  okText={
                    propagationSummary.total > 0
                      ? `Confirmer (${propagationSummary.total} destinataire${propagationSummary.total > 1 ? 's' : ''})`
                      : 'Confirmer sans propagation'
                  }
                  cancelText="Annuler"
                  width={760}
                  zIndex={2100}
                  okButtonProps={{
                    style: {
                      background: colors.gradient,
                      border: 'none',
                      borderRadius: 6,
                    }
                  }}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size={16}>

                    {/* Récapitulatif */}
                    {propagationSummary.total > 0 && (
                      <div style={{
                        background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)',
                        border: `1px solid ${colors.secondary}`,
                        borderRadius: 8,
                        padding: '10px 14px',
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <span>✅</span>
                        <span>
                          Les taux seront appliqués à{' '}
                          {propagationSummary.agCount > 0 && (
                            <strong>{propagationSummary.agCount} agence{propagationSummary.agCount > 1 ? 's' : ''}</strong>
                          )}
                          {propagationSummary.agCount > 0 && propagationSummary.intCount > 0 && ' et '}
                          {propagationSummary.intCount > 0 && (
                            <strong>{propagationSummary.intCount} intermédiaire{propagationSummary.intCount > 1 ? 's' : ''}</strong>
                          )}
                          .
                        </span>
                      </div>
                    )}

                    {propagationSummary.total === 0 && (
                      <div style={{
                        background: '#fff7e6',
                        border: '1px solid #ffd591',
                        borderRadius: 8,
                        padding: '10px 14px',
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8
                      }}>
                        <span>⚠️</span>
                        <span>Les taux seront mis à jour sans être propagés.</span>
                      </div>
                    )}

                    {/* Bloc Agences */}
                    <SelectionBlock
                      title="Agences"
                      icon="🏢"
                      selectionType={agencySelectionType}
                      onChangeType={(v) => { setAgencySelectionType(v); setSelectedAgencies([]); }}
                      items={agencies}
                      loading={loadingAgencies}
                      selectedItems={selectedAgencies}
                      onChangeItems={setSelectedAgencies}
                      itemKey="codeAgence"
                      itemLabel={(a) => a.nom || a.raisonSociale || 'Sans nom'}
                      itemSub={(a) => `Code : ${a.codeAgence || a.codeId}`}
                      previewLabel={`agence${agencies.length > 1 ? 's' : ''}`}
                      colors={colors}
                    />

                    {/* Bloc Intermédiaires */}
                    <SelectionBlock
                      title="Intermédiaires"
                      icon="👤"
                      selectionType={intermediaireSelectionType}
                      onChangeType={(v) => { setIntermediaireSelectionType(v); setSelectedIntermediaires([]); }}
                      items={intermediaires}
                      loading={loadingIntermediaires}
                      selectedItems={selectedIntermediaires}
                      onChangeItems={setSelectedIntermediaires}
                      itemKey="codeIntermediaire"
                      itemLabel={(i) => `${i.personne?.nom ?? ''} ${i.personne?.prenom ?? ''}`.trim() || i.codeIntermediaire}
                      itemSub={(i) => `Code : ${i.codeIntermediaire} · ${i.type || ''}`}
                      previewLabel={`intermédiaire${intermediaires.length > 1 ? 's' : ''}`}
                      colors={colors}
                    />

                  </Space>
                </Modal>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <AddTauxCommission
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedTauxCommission={selectedTauxCommission}
        tauxCommissions={tauxCommissions}
        onRefetch={fetchTauxCommissions}
      />
    </div>
  );
};

export default TauxCommissions;